import React, { useState, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../utils/helpers';
import { hasInspectionMedia, hasCompletionMedia } from '../utils/jobTraffic';

const Sales = ({ onOpenVehicleDetail }) => {
  const { vehicles, updateVehicle, garageInfo } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const salesData = useMemo(() => {
    let salesVehicles = vehicles.filter(veh => veh.status === 'Sales' && !veh.deletedAt);
    
    // Filter by selected site
    if (selectedSite) {
      salesVehicles = salesVehicles.filter(veh => {
        const location = (veh.location && veh.location.trim()) || '';
        if (selectedSite === 'No Location') {
          return !location || location === '';
        } else if (selectedSite === 'Other') {
          // Get available sites from garageInfo
          const sites = garageInfo?.siteNames && garageInfo.siteNames.length > 0
            ? garageInfo.siteNames
            : ['Site A', 'Site B', 'Site C', 'Main Site', 'Storage', 'Other'];
          return location && !sites.includes(location);
        } else {
          return location === selectedSite;
        }
      });
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      salesVehicles = salesVehicles.filter(veh => {
        return [veh.reg, veh.vehicle, veh.summary, veh.technician, veh.contact, veh.motStatus || '']
          .filter(Boolean)
          .some(field => field.toLowerCase().includes(search));
      });
    }

    const prep = [];
    const forSale = [];
    const sold = [];

    salesVehicles.forEach(veh => {
      const stage = (veh.salesStage || 'prep');
      if (stage === 'sold') sold.push(veh);
      else if (stage === 'forSale' || stage === 'deposit') forSale.push(veh);
      else prep.push(veh);
    });

    return { prep, forSale, sold };
  }, [vehicles, searchTerm, selectedSite, garageInfo]);

  // Calculate vehicle counts by site
  const siteCounts = useMemo(() => {
    const salesVehicles = vehicles.filter(veh => veh.status === 'Sales' && !veh.deletedAt);
    const counts = {};
    
    // Get available sites from garageInfo or use defaults
    const sites = garageInfo?.siteNames && garageInfo.siteNames.length > 0
      ? garageInfo.siteNames
      : ['Site A', 'Site B', 'Site C', 'Main Site', 'Storage', 'Other'];
    
    // Initialize all sites with 0
    sites.forEach(site => {
      counts[site] = 0;
    });
    
    // Count vehicles by location
    salesVehicles.forEach(veh => {
      const location = (veh.location && veh.location.trim()) || '';
      if (location && counts.hasOwnProperty(location)) {
        counts[location]++;
      } else if (location) {
        // Handle locations not in the site list - add to "Other"
        if (!counts['Other']) counts['Other'] = 0;
        counts['Other']++;
      } else {
        // Vehicles with no location
        if (!counts['No Location']) counts['No Location'] = 0;
        counts['No Location']++;
      }
    });
    
    return counts;
  }, [vehicles, garageInfo]);

  const handleVehicleClick = (veh) => {
    // Don't open detail modal if we just finished dragging
    if (isDragging) {
      return;
    }
    if (onOpenVehicleDetail) {
      const vehicleIndex = vehicles.findIndex(v => v.reg === veh.reg);
      if (vehicleIndex !== -1) {
        onOpenVehicleDetail(vehicleIndex);
      }
    }
  };

  const handleStageChange = (veh, newStage, e) => {
    e.stopPropagation();
    const vehicleIndex = vehicles.findIndex(v => v.reg === veh.reg);
    if (vehicleIndex !== -1) {
      flushSync(() => {
        updateVehicle(vehicleIndex, { ...veh, salesStage: newStage });
      });
    }
  };

  const handleDragStart = (e, veh) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', veh.reg);
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    // Add a class to prevent click events during drag
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    // Restore opacity after drag ends
    e.currentTarget.style.opacity = '1';
    // Use setTimeout to allow drop event to complete first
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('droppable-hover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('droppable-hover');
  };

  const handleDrop = (e, dropStage) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('droppable-hover');
    
    const reg = e.dataTransfer.getData('text/plain');
    if (!reg) {
      console.warn('No registration found in drag data');
      setIsDragging(false);
      return;
    }
    
    // Find the vehicle in the current vehicles array
    const idx = vehicles.findIndex(v => v.reg === reg);
    if (idx === -1) {
      console.warn(`Vehicle with registration ${reg} not found`);
      setIsDragging(false);
      return;
    }
    
    const vehicle = vehicles[idx];
    if (vehicle.status !== 'Sales') {
      console.warn(`Vehicle ${reg} is not in Sales status`);
      setIsDragging(false);
      return;
    }
    
    // Only update if the stage is actually changing
    const currentStage = vehicle.salesStage || 'prep';
    if (currentStage === dropStage) {
      console.log(`Vehicle ${reg} already in stage ${dropStage}, no update needed`);
      setIsDragging(false);
      return; // No change needed
    }
    
    console.log(`Moving vehicle ${reg} from stage "${currentStage}" to "${dropStage}"`);
    
    // Update the vehicle with the new salesStage, preserving all other properties
    flushSync(() => {
      updateVehicle(idx, { 
        ...vehicle, 
        salesStage: dropStage 
      });
    });
    
    setIsDragging(false);
  };

  const renderLane = (title, list, dropStage) => {
    return (
      <div key={dropStage} className="swimlane">
        <div className="swimlane-header">
          <span className="swimlane-title">{title}</span>
          <span className="swimlane-count">
            {list.length} vehicle{list.length === 1 ? '' : 's'}
          </span>
        </div>
        <div
          className="swimlane-body"
          data-sales-drop-stage={dropStage}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, dropStage)}
          style={{ minHeight: '100px' }}
        >
          {list.map((veh, idx) => {
            const vehicleIdx = vehicles.findIndex(v => v.reg === veh.reg);
            if (vehicleIdx === -1) return null;

            const tags = [];
            if (veh.paid) tags.push(
              <span key="paid" className="vehicle-tag vehicle-tag--success">Paid</span>
            );
            if (veh.collected) tags.push(
              <span key="collected" className="vehicle-tag vehicle-tag--success">Collected</span>
            );
            if (veh.partsOnOrder) tags.push(
              <span key="parts" className="vehicle-tag vehicle-tag--warning">Parts Ordered</span>
            );
            if ((veh.salesStage || 'prep') === 'deposit') tags.push(
              <span key="deposit" className="vehicle-tag">Deposit Taken</span>
            );

            return (
              <div
                key={veh.reg}
                className="vehicle-card"
                draggable
                onDragStart={(e) => handleDragStart(e, veh)}
                onDragEnd={handleDragEnd}
                onClick={() => handleVehicleClick(veh)}
                style={{ cursor: 'pointer' }}
              >
                <span className="vehicle-card-reg">{veh.reg}</span>
                <div className="vehicle-card-title">{veh.vehicle}</div>
                <div className="vehicle-card-meta">
                  <span>{veh.summary}</span>
                  <span>{veh.contact || 'No contact'}</span>
                  <span>{formatCurrency(veh.salePrice || veh.cost || 0)}</span>
                </div>
                {(hasInspectionMedia(veh) || hasCompletionMedia(veh)) && (
                  <div className="vehicle-job-video-pills" aria-label="Video checklist">
                    <span className={`vehicle-job-vpill ${hasInspectionMedia(veh) ? 'vehicle-job-vpill--ok' : 'vehicle-job-vpill--muted'}`}>
                      Inspection {hasInspectionMedia(veh) ? '●' : '○'}
                    </span>
                    <span className={`vehicle-job-vpill ${hasCompletionMedia(veh) ? 'vehicle-job-vpill--ok' : 'vehicle-job-vpill--muted'}`}>
                      Completion {hasCompletionMedia(veh) ? '●' : '○'}
                    </span>
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="vehicle-card-tags">
                    {tags}
                  </div>
                )}
                <div className="vehicle-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="sales-action-btn"
                    onClick={(e) => handleStageChange(veh, 'prep', e)}
                  >
                    Vehicle in Prep
                  </button>
                  <button
                    type="button"
                    className="sales-action-btn"
                    onClick={(e) => handleStageChange(veh, 'forSale', e)}
                  >
                    For Sale
                  </button>
                  <button
                    type="button"
                    className="sales-action-btn"
                    onClick={(e) => handleStageChange(veh, 'sold', e)}
                  >
                    Sold
                  </button>
                  <button
                    type="button"
                    className="sales-action-btn"
                    onClick={(e) => handleStageChange(veh, 'deposit', e)}
                  >
                    Deposit Taken
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="vehicle-inner sales-page">
      <div className="vehicle-header">
        <div>
          <h2>Sales</h2>
          <p className="vehicle-subtitle">Vehicles for sale</p>
        </div>
        <div className="vehicle-controls">
          <label className="search-field">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M10.5 3.75a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5Zm0 1.5a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Zm7.97 11.28 3.15 3.15-1.06 1.06-3.15-3.15 1.06-1.06Z" fill="#4f8df3"/>
            </svg>
            <input 
              id="salesSearch" 
              type="search" 
              placeholder="Search by registration, make, model or technician..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
        </div>
      </div>
      {/* Site Counter */}
      <div className="site-counter-container" style={{
        background: 'var(--light-bg)',
        borderRadius: '16px',
        border: '1px solid rgba(79, 141, 243, 0.16)',
        padding: '16px 20px',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-muted)',
            marginRight: '8px'
          }}>
            Vehicles by Site:
          </span>
          {Object.entries(siteCounts)
            .filter(([site, count]) => count > 0) // Only show sites with vehicles
            .map(([site, count]) => {
              const isSelected = selectedSite === site;
              return (
                <div
                  key={site}
                  onClick={() => setSelectedSite(isSelected ? null : site)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 14px',
                    background: isSelected ? 'var(--primary)' : 'rgba(79, 141, 243, 0.1)',
                    borderRadius: '20px',
                    border: `1px solid ${isSelected ? 'var(--primary)' : 'rgba(79, 141, 243, 0.3)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: isSelected ? '0 2px 8px rgba(79, 141, 243, 0.3)' : 'none'
                  }}
                  title={isSelected ? 'Click to show all vehicles' : `Click to show vehicles at ${site}`}
                >
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: isSelected ? '#ffffff' : 'var(--primary)'
                  }}>
                    {site}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: isSelected ? '#ffffff' : 'var(--primary)',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {count}
                  </span>
                </div>
              );
            })}
          {selectedSite && (
            <button
              onClick={() => setSelectedSite(null)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: '1px solid rgba(79, 141, 243, 0.3)',
                borderRadius: '20px',
                color: 'var(--primary)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              title="Clear filter and show all vehicles"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>
      <div className="vehicle-content">
        <div
          id="salesSwimlanes"
          className="swimlanes"
          data-lane-count={
            [salesData.prep, salesData.forSale, salesData.sold].filter(l => l.length > 0).length
          }
        >
          {salesData.prep.length === 0 && salesData.forSale.length === 0 && salesData.sold.length === 0 ? (
            <div className="empty-state">No vehicles in Sales.</div>
          ) : (
            <>
              {salesData.prep.length > 0 && renderLane('Vehicle in Prep', salesData.prep, 'prep')}
              {salesData.forSale.length > 0 && renderLane('For Sale', salesData.forSale, 'forSale')}
              {salesData.sold.length > 0 && renderLane('Sold', salesData.sold, 'sold')}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sales;
